import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch, FiTag, FiX } from 'react-icons/fi';
import * as wasm from 'sched';

/**
 * Picker that produces a `NodeMatcher` value (internally tagged — see
 * AGENTS.md "Serde tagging convention"). Supports the matcher kinds that
 * point at a single named thing: `TaskId`, `OutcomeId`, `ProblemId`,
 * `ProjectId`, and `Tag`.
 *
 * Logic / AllWork are out of scope — add separate UI when those become
 * needed.
 *
 * Behavior follows the codebase's "search across types" pattern (see
 * `ValueTargetSearch.tsx`):
 *
 * - When unset: a search input with a focus-driven dropdown overlay.
 *   Typing filters across tasks, outcomes, problems, and projects by title.
 *   The bottom of the dropdown always offers "Use as tag: '<query>'" so the
 *   user can produce a `Tag` matcher even though tags can't be enumerated
 *   client-side (no WASM binding for `list_all_tags` yet).
 * - When set: shown as a compact chip with the kind label and a clear button.
 *
 * Output shape is internally tagged:
 *   `{ type: 'TaskId', id }`, `{ type: 'Tag', tag }`, etc.
 */

type MatcherKind = 'TaskId' | 'OutcomeId' | 'ProblemId' | 'ProjectId' | 'Tag';

interface SearchResult {
  kind: Exclude<MatcherKind, 'Tag'>;
  id: string;
  title: string;
}

const KIND_COLOR: Record<MatcherKind, string> = {
  TaskId: 'bg-blue-500/20 text-blue-300',
  OutcomeId: 'bg-purple-500/20 text-purple-300',
  ProblemId: 'bg-red-500/20 text-red-300',
  ProjectId: 'bg-orange-500/20 text-orange-300',
  Tag: 'bg-teal-500/20 text-teal-300',
};

const KIND_LABEL: Record<MatcherKind, string> = {
  TaskId: 'Task',
  OutcomeId: 'Outcome',
  ProblemId: 'Problem',
  ProjectId: 'Project',
  Tag: 'Tag',
};

function summarizeMatcher(
  matcher: wasm.NodeMatcher | null,
  database: wasm.WrappedDatabase | null,
): { kind: MatcherKind; label: string } | null {
  if (!matcher) return null;
  // After string-form 'AllWork' is reachable; fall through gracefully.
  if (typeof (matcher as any).type !== 'string') return null;
  const m = matcher as { type: string; id?: string; tag?: string };
  switch (m.type) {
    case 'TaskId': {
      const id = m.id!;
      const title = database?.tasks()[id]?.title() ?? id;
      return { kind: 'TaskId', label: title };
    }
    case 'OutcomeId': {
      const id = m.id!;
      const title = database?.outcomes()[id]?.title() ?? id;
      return { kind: 'OutcomeId', label: title };
    }
    case 'ProblemId': {
      const id = m.id!;
      const title = database?.problems()[id]?.title() ?? id;
      return { kind: 'ProblemId', label: title };
    }
    case 'ProjectId': {
      const id = m.id!;
      const title = database?.projects()[id]?.name() ?? id;
      return { kind: 'ProjectId', label: title };
    }
    case 'Tag':
      return { kind: 'Tag', label: m.tag! };
    default:
      return null;
  }
}

interface Props {
  database: wasm.WrappedDatabase | null;
  value: wasm.NodeMatcher | null;
  onChange: (matcher: wasm.NodeMatcher | null) => void;
  placeholder?: string;
  /** Result limit per type. */
  limit?: number;
}

const NodeMatcherPicker: React.FC<Props> = ({
  database,
  value,
  onChange,
  placeholder = 'Search tasks, outcomes, problems, projects… or type a tag',
  limit = 8,
}) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const results = useMemo<SearchResult[]>(() => {
    if (!database || query.trim().length === 0) return [];
    const q = query.trim().toLowerCase();
    const out: SearchResult[] = [];

    let tasksAdded = 0;
    for (const [id, t] of Object.entries(database.tasks())) {
      if (tasksAdded >= limit) break;
      const title = (t as any).title();
      if (title.toLowerCase().includes(q)) {
        out.push({ kind: 'TaskId', id, title });
        tasksAdded += 1;
      }
    }
    let outcomesAdded = 0;
    for (const [id, o] of Object.entries(database.outcomes())) {
      if (outcomesAdded >= limit) break;
      const title = (o as any).title();
      if (title.toLowerCase().includes(q)) {
        out.push({ kind: 'OutcomeId', id, title });
        outcomesAdded += 1;
      }
    }
    let problemsAdded = 0;
    for (const [id, p] of Object.entries(database.problems())) {
      if (problemsAdded >= limit) break;
      const title = (p as any).title();
      if (title.toLowerCase().includes(q)) {
        out.push({ kind: 'ProblemId', id, title });
        problemsAdded += 1;
      }
    }
    let projectsAdded = 0;
    for (const [id, p] of Object.entries(database.projects())) {
      if (projectsAdded >= limit) break;
      const name = (p as any).name();
      if (name.toLowerCase().includes(q)) {
        out.push({ kind: 'ProjectId', id, title: name });
        projectsAdded += 1;
      }
    }

    return out;
  }, [database, query, limit]);

  const summary = summarizeMatcher(value, database);

  // ── Selected: chip view ────────────────────────────────────────
  if (summary) {
    return (
      <div className="flex items-center gap-2 bg-cm-bg-elevated border border-cm-border-subtle rounded-lg px-3 py-2">
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 ${KIND_COLOR[summary.kind]}`}>
          {KIND_LABEL[summary.kind]}
        </span>
        <span className="text-sm text-cm-text-primary truncate flex-1">{summary.label}</span>
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setQuery('');
          }}
          className="text-cm-text-muted hover:text-cm-text-primary text-xs"
          aria-label="Clear selection"
        >
          <FiX className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // ── Unselected: search view ────────────────────────────────────
  const trimmedQuery = query.trim();
  const showTagAffordance = trimmedQuery.length > 0;

  const pickResult = (r: SearchResult) => {
    onChange({ type: r.kind, id: r.id } as unknown as wasm.NodeMatcher);
    setQuery('');
    setFocused(false);
  };

  const pickTag = () => {
    if (!trimmedQuery) return;
    onChange({ type: 'Tag', tag: trimmedQuery } as unknown as wasm.NodeMatcher);
    setQuery('');
    setFocused(false);
  };

  const showDropdown = focused && (results.length > 0 || showTagAffordance);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cm-text-muted pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2 bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:border-cm-accent-orange focus:ring-cm-accent-orange focus:outline-none"
        />
      </div>

      {showDropdown && (
        <ul className="absolute z-20 left-0 right-0 mt-1 bg-cm-bg-surface border border-cm-border-mid rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {results.map((r) => (
            <li key={`${r.kind}-${r.id}`}>
              <button
                type="button"
                onClick={() => pickResult(r)}
                className="w-full px-3 py-2 text-sm text-left text-cm-text-primary hover:bg-cm-bg-hover transition-colors flex items-center gap-2"
              >
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 ${KIND_COLOR[r.kind]}`}>
                  {KIND_LABEL[r.kind]}
                </span>
                <span className="truncate">{r.title}</span>
              </button>
            </li>
          ))}

          {showTagAffordance && (
            <li className={results.length > 0 ? 'border-t border-cm-border-subtle' : ''}>
              <button
                type="button"
                onClick={pickTag}
                className="w-full px-3 py-2 text-sm text-left text-cm-accent-teal hover:bg-cm-bg-hover transition-colors flex items-center gap-2"
              >
                <FiTag className="w-3.5 h-3.5 shrink-0" />
                <span>
                  Use as tag: <span className="font-medium">"{trimmedQuery}"</span>
                </span>
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default NodeMatcherPicker;
